// SearchPage.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  searchProducts,
  getBestsellerProducts,
  getCategories,
  getSubCategories,
  getCompleteProducts,
} from "../api";
import FilterSidebar from "../Bestsellers/FilterSidebar";
import { useCurrency } from "../../Context/CurrencyContext";
import "./SearchPage.css";

// Cache for search API responses
const apiCache = {
  search: {},
};

// Base URL for API (used to prepend relative image paths)
const API_BASE_URL = "https://volumaire.backendapihub.com";

// Default image for products without an image
const DEFAULT_IMAGE = "/images/image_not_found 1.svg";
// Ultimate fallback
const FALLBACK_IMAGE = "https://via.placeholder.com/300x300?text=No+Image";

// Search Icon SVG Component
const SearchIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 16 16"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"></path>
  </svg>
);

const SearchPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice } = useCurrency();

  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [allSearchResults, setAllSearchResults] = useState([]);
  const [isViewAllMode, setIsViewAllMode] = useState(false);

  // Categories & Subcategories from API
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Filter state
  const [filters, setFilters] = useState({
    categories: [],
    subcategories: [],
    brands: [],
    types: [],
    availability: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    categories: [],
    subcategories: [],
    brands: [],
    types: [],
    availability: [],
  });
  const [openFilter, setOpenFilter] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState(null);

  const searchTimeoutRef = useRef(null);

  // Static suggestions
  const suggestions = ["oil", "argan oil", "oily hair"];
  const collections = ["Essential oils"];

  // ---------- HIDE NAVBAR & ANNOUNCEMENT BAR WHEN THIS PAGE IS MOUNTED ----------
  useEffect(() => {
    document.body.classList.add("search-page-active");
    return () => {
      document.body.classList.remove("search-page-active");
    };
  }, []);

  // ---------- Helper: check if search query is oil-related ----------
  const isOilRelatedSearch = useCallback((query) => {
    if (!query) return false;
    const lowerQuery = query.toLowerCase().trim();
    const oilKeywords = ["oil", "argan", "olive", "coconut", "essential", "hair oil", "argan oil", "oily"];
    return oilKeywords.some(keyword => lowerQuery.includes(keyword));
  }, []);

  // ---------- Helper: extract price ----------
  const extractPrice = (product) => {
    if (!product) return 0;
    const priceFields = [
      "price", "salePrice", "regularPrice", "productPrice",
      "originalPrice", "discountedPrice", "offerPrice", "finalPrice",
      "Price", "SalePrice", "RegularPrice", "ProductPrice",
    ];
    for (const field of priceFields) {
      const value = product[field];
      if (value !== undefined && value !== null) {
        if (typeof value === "number") return value;
        if (typeof value === "string") {
          const cleaned = value.replace(/[$,€£¥\s]/g, "");
          const parsed = parseFloat(cleaned);
          if (!isNaN(parsed)) return parsed;
        }
        if (typeof value === "object" && value !== null) {
          if (value.amount !== undefined) {
            const amt = value.amount;
            if (typeof amt === "number") return amt;
            if (typeof amt === "string") {
              const parsed = parseFloat(amt.replace(/[$,€£¥\s]/g, ""));
              if (!isNaN(parsed)) return parsed;
            }
          }
        }
      }
    }
    for (const key in product) {
      if (key.toLowerCase().includes("price")) {
        const value = product[key];
        if (typeof value === "number") return value;
        if (typeof value === "string") {
          const cleaned = value.replace(/[$,€£¥\s]/g, "");
          const parsed = parseFloat(cleaned);
          if (!isNaN(parsed)) return parsed;
        }
      }
    }
    return 0;
  };

  // ---------- Helper: extract original price for discount ----------
  const extractOriginalPrice = (product) => {
    if (!product) return null;
    const originalFields = [
      "originalPrice", "regularPrice", "compareAtPrice", "oldPrice",
      "listPrice", "rrp", "msrp", "OriginalPrice", "RegularPrice"
    ];
    for (const field of originalFields) {
      const value = product[field];
      if (value !== undefined && value !== null) {
        if (typeof value === "number") return value;
        if (typeof value === "string") {
          const cleaned = value.replace(/[$,€£¥\s]/g, "");
          const parsed = parseFloat(cleaned);
          if (!isNaN(parsed)) return parsed;
        }
      }
    }
    return null;
  };

  // ---------- Helper: extract discount percentage ----------
  const extractDiscountPercent = (product, price, originalPrice) => {
    if (product.discountPercent && typeof product.discountPercent === "number") {
      return product.discountPercent;
    }
    if (product.discountPercentage && typeof product.discountPercentage === "number") {
      return product.discountPercentage;
    }
    if (originalPrice && originalPrice > price) {
      const discount = Math.round(((originalPrice - price) / originalPrice) * 100);
      return discount > 0 ? discount : null;
    }
    return null;
  };

  // ---------- Helper: extract image URL ----------
  const extractImageUrl = (product) => {
    if (!product) return DEFAULT_IMAGE;

    const toAbsoluteUrl = (url) => {
      if (!url || typeof url !== "string") return null;
      if (url.startsWith("http://") || url.startsWith("https://")) return url;
      if (url.startsWith("//")) return `https:${url}`;
      if (url.startsWith("/")) return `${API_BASE_URL}${url}`;
      return `${API_BASE_URL}/${url}`;
    };

    const stringFields = [
      "image", "img", "picture", "photo", "mainImage", "featuredImage",
      "thumbnail", "imageUrl", "imgUrl", "productImage", "primaryImage",
      "featured_image", "main_image", "product_img", "image_url", "img_url",
      "photo_url", "picture_url", "thumbnail_url", "image_path", "img_path",
      "imageSrc", "imgSrc", "image_src", "img_src", "image_link", "img_link",
      "url", "src", "path", "file", "uri", "link",
    ];
    for (const field of stringFields) {
      const val = product[field];
      if (val && typeof val === "string") {
        const url = toAbsoluteUrl(val);
        if (url) return url;
      }
    }

    const arrayFields = ["images", "media", "gallery", "photos", "pictures", "imageList", "imgList"];
    for (const arrField of arrayFields) {
      const arr = product[arrField];
      if (Array.isArray(arr) && arr.length > 0) {
        for (const item of arr) {
          if (typeof item === "string") {
            const url = toAbsoluteUrl(item);
            if (url) return url;
          } else if (item && typeof item === "object") {
            const objFields = ["url", "src", "image", "img", "path", "file", "uri", "link", "href"];
            for (const f of objFields) {
              if (item[f] && typeof item[f] === "string") {
                const url = toAbsoluteUrl(item[f]);
                if (url) return url;
              }
            }
          }
        }
      }
    }

    if (product.image && typeof product.image === "object") {
      const nestedFields = ["url", "src", "path", "file", "uri", "link"];
      for (const f of nestedFields) {
        if (product.image[f] && typeof product.image[f] === "string") {
          const url = toAbsoluteUrl(product.image[f]);
          if (url) return url;
        }
      }
    }

    if (product.product && typeof product.product === "object") {
      const subUrl = extractImageUrl(product.product);
      if (subUrl && subUrl !== DEFAULT_IMAGE) return subUrl;
    }

    if (product.data && typeof product.data === "object") {
      const subUrl = extractImageUrl(product.data);
      if (subUrl && subUrl !== DEFAULT_IMAGE) return subUrl;
    }

    return DEFAULT_IMAGE;
  };

  // ---------- Normalize products with discount info ----------
  const normalizeProducts = (productArray) => {
    if (!Array.isArray(productArray)) return [];
    return productArray.map((p) => {
      const price = extractPrice(p);
      const originalPrice = extractOriginalPrice(p);
      const discountPercent = extractDiscountPercent(p, price, originalPrice);
      return {
        _id: p._id || p.id,
        name: p.name || p.productName,
        image: extractImageUrl(p),
        description: p.description || "",
        price: price,
        originalPrice: originalPrice,
        discountPercent: discountPercent,
        brand: p.brand || "MOKOSH",
        type: p.type || p.category?.name || "Product",
        inStock: p.inStock !== undefined ? p.inStock : true,
        category: p.category || null,
        subcategory: p.subcategory || null,
        categoryId: p.category?._id || p.categoryId,
        subcategoryId: p.subcategory?._id || p.subcategoryId,
        isBestseller: p.isBestseller || false,
      };
    });
  };

  // ---------- Fetch categories & subcategories ----------
  useEffect(() => {
    const fetchFiltersData = async () => {
      try {
        const [categoriesRes, subcategoriesRes] = await Promise.all([
          getCategories({ useCache: true }),
          getSubCategories({ useCache: true }),
        ]);
        const cats = categoriesRes?.data?.categories || categoriesRes?.data || categoriesRes || [];
        const subs = subcategoriesRes?.data?.subcategories || subcategoriesRes?.data || subcategoriesRes || [];
        setCategories(Array.isArray(cats) ? cats : []);
        setSubcategories(Array.isArray(subs) ? subs : []);
      } catch (err) {
        console.error("Failed to load categories/subcategories:", err);
        setCategories([]);
        setSubcategories([]);
      }
    };
    fetchFiltersData();
  }, []);

  // ---------- Helper to extract products from API response ----------
  const extractProductsFromResponse = (response) => {
    if (!response) return [];
    if (response.success && response.data?.products && Array.isArray(response.data.products)) {
      return response.data.products;
    }
    if (response.success && Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (response.data?.products && Array.isArray(response.data.products)) {
      return response.data.products;
    }
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    if (response.products && Array.isArray(response.products)) {
      return response.products;
    }
    return [];
  };

  // ---------- Fetch ALL products on initial load ----------
  useEffect(() => {
    const fetchAllProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await getCompleteProducts({ useCache: false });
        let allProducts = extractProductsFromResponse(response);

        if (allProducts.length === 0) {
          console.warn("All-products endpoint returned empty, falling back to bestsellers.");
          const bestsellersRes = await getBestsellerProducts({ useCache: false });
          allProducts = extractProductsFromResponse(bestsellersRes);
        }

        const normalized = normalizeProducts(allProducts);
        setProducts(normalized);
        setFilteredProducts(normalized);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setError("Could not load products. Please try again later.");
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchAllProducts();
  }, []);

  // ---------- Search handler ----------
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Exit view all mode when search query changes
    setIsViewAllMode(false);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim()) {
      setLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 500);
    } else {
      refetchAllProducts();
      setLoading(false);
    }
  };

  const refetchAllProducts = async () => {
    setLoadingProducts(true);
    setIsViewAllMode(false);
    try {
      const response = await getCompleteProducts({ skipCache: true });
      let allProducts = extractProductsFromResponse(response);

      if (allProducts.length === 0) {
        const bestsellersRes = await getBestsellerProducts({ skipCache: true });
        allProducts = extractProductsFromResponse(bestsellersRes);
      }

      const normalized = normalizeProducts(allProducts);
      setProducts(normalized);
      setFilteredProducts(normalized);
      setAllSearchResults([]);
      setError(null);
    } catch (err) {
      console.error("Failed to refetch products:", err);
      setError("Could not reload products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const performSearch = async (query) => {
    try {
      const cacheKey = query.toLowerCase().trim();
      if (apiCache.search[cacheKey]) {
        const cached = apiCache.search[cacheKey];
        setAllSearchResults(cached);
        setProducts(cached);
        applyFiltersToProducts(cached, appliedFilters);
        setLoading(false);
        setError(null);
        return;
      }

      const response = await searchProducts(query, { skipCache: false });
      const searchResults = extractProductsFromResponse(response);
      const normalized = normalizeProducts(searchResults);
      apiCache.search[cacheKey] = normalized;
      setAllSearchResults(normalized);
      setProducts(normalized);
      applyFiltersToProducts(normalized, appliedFilters);
      setError(null);
    } catch (err) {
      console.error("Search error:", err);
      setError("Failed to search products. Please try again.");
      setProducts([]);
      setFilteredProducts([]);
      setAllSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- VIEW ALL handler - Navigate to SearchResultsPage ----------
  const handleViewAll = () => {
    if (searchQuery.trim()) {
      navigate("/search/results", { state: { query: searchQuery } });
    }
  };

  // ---------- Close handler - Go back to previous page ----------
  const handleClose = () => {
    navigate(-1);
  };

  // ---------- Apply filters ----------
  const applyFiltersToProducts = (productsArray, filtersToApply) => {
    let filtered = [...productsArray];

    if (filtersToApply.categories.length > 0) {
      filtered = filtered.filter((product) => {
        const productCatId = product.categoryId || product.category?._id;
        return productCatId && filtersToApply.categories.includes(String(productCatId));
      });
    }

    if (filtersToApply.subcategories.length > 0) {
      filtered = filtered.filter((product) => {
        const productSubId = product.subcategoryId || product.subcategory?._id;
        return productSubId && filtersToApply.subcategories.includes(String(productSubId));
      });
    }

    if (filtersToApply.brands.length > 0) {
      filtered = filtered.filter((product) =>
        product.brand && filtersToApply.brands.includes(product.brand)
      );
    }

    if (filtersToApply.types.length > 0) {
      filtered = filtered.filter((product) =>
        product.type && filtersToApply.types.includes(product.type)
      );
    }

    if (filtersToApply.availability.length === 1) {
      const isInStock = filtersToApply.availability[0] === "inStock";
      filtered = filtered.filter((product) => product.inStock === isInStock);
    }

    setFilteredProducts(filtered);
  };

  const productCounts = useMemo(() => {
    const counts = {
      categories: {},
      subcategories: {},
      brands: {},
      types: {},
      availability: { inStock: 0, outOfStock: 0 },
    };
    if (!Array.isArray(products) || products.length === 0) return counts;

    products.forEach((product) => {
      if (product.categoryId) {
        const catId = String(product.categoryId);
        counts.categories[catId] = (counts.categories[catId] || 0) + 1;
      } else if (product.category?._id) {
        const catId = String(product.category._id);
        counts.categories[catId] = (counts.categories[catId] || 0) + 1;
      }

      if (product.subcategoryId) {
        const subId = String(product.subcategoryId);
        counts.subcategories[subId] = (counts.subcategories[subId] || 0) + 1;
      } else if (product.subcategory?._id) {
        const subId = String(product.subcategory._id);
        counts.subcategories[subId] = (counts.subcategories[subId] || 0) + 1;
      }

      if (product.brand) {
        const brand = String(product.brand).trim();
        counts.brands[brand] = (counts.brands[brand] || 0) + 1;
      }

      if (product.type) {
        const type = String(product.type).trim();
        counts.types[type] = (counts.types[type] || 0) + 1;
      }

      if (product.inStock === true) {
        counts.availability.inStock++;
      } else {
        counts.availability.outOfStock++;
      }
    });
    return counts;
  }, [products]);

  const applyFilters = useCallback(() => {
    applyFiltersToProducts(products, appliedFilters);
  }, [products, appliedFilters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setOpenFilter(false);
  };

  const handleResetFilters = () => {
    const reset = {
      categories: [],
      subcategories: [],
      brands: [],
      types: [],
      availability: [],
    };
    setFilters(reset);
    setAppliedFilters(reset);
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      performSearch(searchQuery);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setIsViewAllMode(false);
    performSearch(suggestion);
  };

  const handleImageError = (e) => {
    e.target.onerror = null;
    const currentSrc = e.target.src;
    if (
      currentSrc.includes(DEFAULT_IMAGE) ||
      currentSrc === window.location.origin + DEFAULT_IMAGE
    ) {
      e.target.src = FALLBACK_IMAGE;
    } else {
      e.target.src = DEFAULT_IMAGE;
    }
  };

  // Determine display products based on view all mode
  const getDisplayProducts = () => {
    if (isSearchMode && isViewAllMode) {
      return filteredProducts;
    }
    if (isSearchMode && !isViewAllMode) {
      return filteredProducts.slice(0, 8);
    }
    return filteredProducts;
  };

  // Determine if we are in search mode (has search query) or not
  const isSearchMode = searchQuery && searchQuery.trim() !== "";
  // Show sidebar only for oil-related searches
  const showSidebar = isSearchMode && isOilRelatedSearch(searchQuery) && !isViewAllMode;

  const displayProducts = getDisplayProducts();

  // Show VIEW ALL button whenever there are any search results
  const hasMoreResults = isSearchMode && filteredProducts.length > 0;

  // ---- Helper: highlight matched query text in product name ----
  const highlightMatch = (text, query) => {
    if (!text || !query) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <strong key={i}>{part}</strong> : part
    );
  };

  return (
    <div className="search-page">
      <div className="search-bar-wrapper">
        <div className="search-bar">
          <span className="search-icon">
            <SearchIcon />
          </span>
          <input
            type="text"
            placeholder="Find a product..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearchSubmit}
          />
          {/* Clear input button inside search bar */}
          {searchQuery && (
            <span
              className="search-clear-inner"
              onClick={() => {
                setSearchQuery("");
                refetchAllProducts();
              }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </span>
          )}
        </div>
        {/* Close button - goes back to previous page */}
        <span className="search-close" onClick={handleClose}>
          ✕
        </span>
      </div>

      <FilterSidebar
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={handleApplyFilters}
        productCounts={productCounts}
        categories={categories}
        subcategories={subcategories}
      />

      {(loading || loadingProducts) && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{loading ? "Searching products..." : "Loading products..."}</p>
        </div>
      )}

      {error && !loading && !loadingProducts && (
        <div className="error-state">
          <p>{error}</p>
          <button
            className="retry-btn"
            onClick={() => {
              setError(null);
              if (searchQuery) {
                performSearch(searchQuery);
              } else {
                refetchAllProducts();
              }
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {!loading && !loadingProducts && !error && displayProducts.length === 0 && isSearchMode && (
        <div className="no-results">
          <p>
            No products found for "{searchQuery}". Try a different search
            or filter.
          </p>
        </div>
      )}

      {/* ==================== SEARCH RESULTS VIEW ==================== */}
      {!loading && !loadingProducts && !error && isSearchMode && displayProducts.length > 0 && (
        <div className={`search-results-container ${!showSidebar ? "full-width" : ""}`}>
          <div className="search-results-main">
            <h3 className="products-list-title">
              Products {isViewAllMode ? `(All ${filteredProducts.length} results)` : ""}
            </h3>

            {/* ---- LIST LAYOUT: 2 columns of horizontal product rows ---- */}
            <div className="search-list-grid">
              {displayProducts.map((item) => (
                <div
                  key={item._id}
                  className="search-list-item"
                  onClick={() => handleProductClick(item._id)}
                >
                  {/* Product Image */}
                  <div className="search-list-img-wrap">
                    <img
                      src={item.image}
                      alt={item.name}
                      onError={handleImageError}
                    />
                    {!item.inStock && (
                      <span className="out-of-stock-badge">Out of Stock</span>
                    )}
                    {item.isBestseller && !item.discountPercent && (
                      <span className="bestseller-badge">Bestseller</span>
                    )}
                    {item.discountPercent && (
                      <span className="discount-badge">-{item.discountPercent}%</span>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="search-list-info">
                    <p className="search-product-brand">{item.brand}</p>
                    <p className="search-product-name">
                      {highlightMatch(item.name, searchQuery)}
                    </p>
                    <div className="price-wrapper">
                      <p className="search-product-price">
                        {formatPrice(item.price)}
                      </p>
                      {item.originalPrice && item.originalPrice > item.price && (
                        <>
                          <span className="old-price">{formatPrice(item.originalPrice)}</span>
                          {item.discountPercent && (
                            <span className="discount-percent">-{item.discountPercent}%</span>
                          )}
                        </>
                      )}
                    </div>
                    {!item.inStock && (
                      <div className="out-of-stock-label">Out of Stock</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* VIEW ALL button */}
            {hasMoreResults && (
              <div className="view-all-wrapper">
                <button className="view-all-button" onClick={handleViewAll}>
                  VIEW ALL FOR "{searchQuery.toUpperCase()}"
                </button>
              </div>
            )}
          </div>

          {/* Right sidebar – Suggestions & Collections */}
          {showSidebar && (
            <div className="search-results-sidebar">
              <div className="sidebar-section">
                <h3 className="sidebar-title">Suggestions</h3>
                <div className="suggestions-list">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              </div>

              <div className="sidebar-section">
                <h3 className="sidebar-title">Collections</h3>
                <div className="collections-list">
                  {collections.map((collection, index) => (
                    <div key={index} className="collection-item">
                      {collection}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== TOP SELLERS GRID (no search query) ==================== */}
      {!loading && !loadingProducts && !error && !isSearchMode && filteredProducts.length > 0 && (
        <div className="top-sellers-section">
          <h2 className="top-sellers-title">Top Sellers</h2>
          <div className="search-products">
            {filteredProducts.slice(0, 4).map((item) => (
              <div
                key={item._id}
                className="search-card"
                onClick={() => handleProductClick(item._id)}
              >
                <div className="search-img-wrap">
                  <img
                    src={item.image}
                    alt={item.name}
                    onError={handleImageError}
                  />
                  {!item.inStock && (
                    <span className="out-of-stock-badge">Out of Stock</span>
                  )}
                  {item.isBestseller && !item.discountPercent && (
                    <span className="bestseller-badge">Bestseller</span>
                  )}
                  {item.discountPercent && (
                    <span className="discount-badge">-{item.discountPercent}%</span>
                  )}
                </div>
                <p className="search-product-brand">{item.brand}</p>
                <p className="search-product-name">{item.name}</p>
                <div className="price-wrapper">
                  <p className="search-product-price">
                    {formatPrice(item.price)}
                  </p>
                  {item.originalPrice && item.originalPrice > item.price && (
                    <>
                      <span className="old-price">{formatPrice(item.originalPrice)}</span>
                      {item.discountPercent && (
                        <span className="discount-percent">-{item.discountPercent}%</span>
                      )}
                    </>
                  )}
                </div>
                {!item.inStock && (
                  <div className="out-of-stock-label">Out of Stock</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;